import moment from 'moment';

/** Mirrors @ampsec/platform-client AnalyticsChartDatasetConfig for use in validation/build. */
export interface AnalyticsChartDatasetConfig {
    dataExpression: string;
    label: string;
    yAxisID?: string;
    color?: string;
    fill?: boolean;
    type?: 'line' | 'bar';
}

/** Mirrors @ampsec/platform-client AnalyticsChartConfig. */
export interface AnalyticsChartConfig {
    id: string;
    title: string;
    subtitle: string;
    datasets: AnalyticsChartDatasetConfig[];
}

/** Period granularity for analytics metrics (matches analytics API periodType). */
export type AnalyticsPeriodType = 'Week' | 'Month' | 'Quarter' | 'Year';

/** Formats a UTC period timestamp for chart labels based on periodType. */
export function formatAnalyticsPeriodLabel(periodIso: string, periodType: AnalyticsPeriodType): string {
    const date = moment.utc(periodIso);
    if (!date.isValid()) {
        return periodIso;
    }
    switch (periodType) {
        case 'Week':
            return date.format('DD MMM');
        case 'Month':
        case 'Quarter':
            return date.format('MMM YYYY');
        case 'Year':
            return date.format('YYYY');
        default:
            return date.format('DD MMM');
    }
}

/** Numeric row fields allowed in dataExpression (matches FindingMetricsSeriesDto). */
export const ALLOWED_ROW_FIELDS = new Set([
    'averageAge',
    'averageOpenedAge',
    'averageClosedAge',
    'findingsOpened',
    'count',
    'findingsClosed',
    'engaged',
    'healed',
    'averageDaysToHeal',
]);

const ALLOWED_OPS = new Set(['+', '-', '*', '/']);
const OP_PRIORITY: Record<string, number> = { '+': 0, '-': 0, '*': 1, '/': 1 };

type Token = { type: 'id'; value: string } | { type: 'op'; value: string } | { type: 'paren'; value: '(' | ')' };

/** Tokenizes a data expression into a list of tokens. */
function tokenize(expr: string): Token[] {
    const s = expr.replace(/\s+/g, ' ').trim();
    const tokens: Token[] = [];
    let i = 0;
    while (i < s.length) {
        if (s[i] === '(' || s[i] === ')') {
            tokens.push({ type: 'paren', value: s[i] as '(' | ')' });
            i++;
            continue;
        }
        if (ALLOWED_OPS.has(s[i])) {
            tokens.push({ type: 'op', value: s[i] });
            i++;
            continue;
        }
        if (/[a-zA-Z_][a-zA-Z0-9_]*/.test(s[i])) {
            let id = '';
            while (i < s.length && /[a-zA-Z0-9_]/.test(s[i])) {
                id += s[i];
                i++;
            }
            tokens.push({ type: 'id', value: id });
            continue;
        }
        i++;
    }
    return tokens;
}

/**
 * Validates that the expression only uses allowed field names and + - * / ( ).
 * Returns null if valid, or an error message if invalid.
 */
export function validateDataExpression(expression: string): string | null {
    if (!expression || typeof expression !== 'string') {
        return 'Expression is empty or invalid';
    }
    const tokens = tokenize(expression);
    for (const t of tokens) {
        if (t.type === 'id' && !ALLOWED_ROW_FIELDS.has(t.value)) {
            return `Invalid field "${t.value}" in expression. Allowed: ${[...ALLOWED_ROW_FIELDS].join(', ')}`;
        }
        if (t.type === 'op' && !ALLOWED_OPS.has(t.value)) {
            return `Invalid operator in expression`;
        }
    }
    // Basic balance check for parens
    let depth = 0;
    for (const t of tokens) {
        if (t.type === 'paren') {
            if (t.value === '(') {
                depth++;
            } else {
                depth--;
            }
            if (depth < 0) {
                return 'Mismatched parentheses';
            }
        }
    }
    if (depth !== 0) {
        return 'Mismatched parentheses';
    }
    return null;
}

/** Hex (3, 4, 6, 8 digits) or rgba(r,g,b,a). Returns null if valid, error message if invalid. */
export function validateChartColor(color: string | undefined): string | null {
    if (color === undefined || color === '') {
        return null;
    }
    const hex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;
    if (hex.test(color)) {
        return null;
    }
    const rgba = /^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*[\d.]+\s*)?\)$/;
    if (rgba.test(color)) {
        return null;
    }
    return `Invalid color "${color}". Use hex (#RGB, #RRGGBB, #RRGGBBAA) or rgba(r,g,b,a).`;
}

/**
 * Evaluates a safe expression for one row. Division by zero returns 0.
 * Call validateDataExpression first; behavior is undefined for invalid expressions.
 */
export function evaluateDataExpression(expression: string, row: Record<string, number | string | undefined>): number {
    const tokens = tokenize(expression);
    const values: number[] = [];
    const ops: string[] = [];

    function apply(op: string): void {
        const b = values.pop();
        const a = values.pop();
        if (a === undefined || b === undefined) {
            return;
        }
        if (op === '+') {
            values.push(a + b);
        } else if (op === '-') {
            values.push(a - b);
        } else if (op === '*') {
            values.push(a * b);
        } else if (op === '/') {
            values.push(b === 0 ? 0 : a / b);
        }
    }

    /** Flushes operators until the predicate is false. */
    function flushUntil(pred: (o: string) => boolean): void {
        while (ops.length > 0 && pred(ops[ops.length - 1])) {
            const o = ops.pop()!;
            if (o !== '(') {
                apply(o);
            }
        }
    }

    for (const t of tokens) {
        if (t.type === 'id') {
            const v = row[t.value];
            const n = typeof v === 'number' && !Number.isNaN(v) ? v : Number(v);
            values.push(Number.isFinite(n) ? n : 0);
            continue;
        }
        if (t.type === 'op') {
            const p = OP_PRIORITY[t.value] ?? 0;
            flushUntil((o) => o !== '(' && (OP_PRIORITY[o] ?? -1) >= p);
            ops.push(t.value);
            continue;
        }
        if (t.type === 'paren') {
            if (t.value === '(') {
                ops.push('(');
            } else {
                flushUntil((o) => o !== '(');
                if (ops[ops.length - 1] === '(') {
                    ops.pop();
                }
            }
        }
    }
    flushUntil(() => true);
    const result = values[0];
    return typeof result === 'number' && Number.isFinite(result) ? result : 0;
}

/**
 * Validates a full chart config (all dataset expressions and colors).
 * Returns null if valid, or an error message for the chart.
 */
export function validateChartConfig(chart: AnalyticsChartConfig): string | null {
    if (!chart.datasets?.length) {
        return 'Chart has no datasets';
    }
    for (const ds of chart.datasets) {
        const exprErr = validateDataExpression(ds.dataExpression);
        if (exprErr) {
            return `Dataset "${ds.label}": ${exprErr}`;
        }
        const colorErr = validateChartColor(ds.color);
        if (colorErr) {
            return `Dataset "${ds.label}": ${colorErr}`;
        }
    }
    return null;
}

/** Row shape for analytics metrics (period + numeric fields). */
export type AnalyticsChartRowLike = { period: string } & Record<string, number | string | undefined>;

/**
 * Builds CartesianDataset from chart config and rows. Assumes config was already validated.
 */
export function buildChartDataset(
    chart: AnalyticsChartConfig,
    rows: AnalyticsChartRowLike[],
    periodType: AnalyticsPeriodType = 'Week',
): {
    labels: string[];
    data: { data: number[]; label: string; yAxisID?: string; color?: string; fill?: boolean; type?: 'line' | 'bar' }[];
} {
    const labels = rows.map((r) => formatAnalyticsPeriodLabel(r.period, periodType));
    const data = chart.datasets.map((ds: AnalyticsChartDatasetConfig) => ({
        data: rows.map((row) => evaluateDataExpression(ds.dataExpression, row)),
        label: ds.label,
        ...(ds.yAxisID && { yAxisID: ds.yAxisID }),
        ...(ds.color && { color: ds.color }),
        ...(ds.fill !== undefined && { fill: ds.fill }),
        ...(ds.type && { type: ds.type }),
    }));
    return { labels, data };
}
